import { Injectable } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { AppSettingsService, MessageTransportService } from '@nx-fe/services';
import * as atlasService from 'azure-maps-rest';
import * as atlas from 'azure-maps-control';
import {
  Aborter,
  CalculateRouteDirectionsOptions,
  Pipeline,
  SearchAddressReverseOptions,
  SearchAddressReverseResponse,
  SearchFuzzyOptions,
} from 'azure-maps-rest';
import { Enums } from './enums/enums';

@Injectable({
  providedIn: 'root',
})
export class AzureWrapperServices {
  private pipeline: atlasService.Pipeline;

  constructor(
    private _appSettingsService: AppSettingsService,
    private _analytics: AnalyticsService,
    private _messageTransportService: MessageTransportService
  ) {
    this._appSettingsService.loadAzureKey().then(resp => {
      this.pipeline = atlasService.MapsURL.newPipeline(new atlasService.SasCredential(resp.azureApiKey));
      this._messageTransportService.event('azureWrapperPipelineBuilt').emit(true);
    });
  }

  routeURL() {
    return new XsRouteURL(this._analytics, this.pipeline);
  }

  searchURL() {
    return new XsSearchURL(this._analytics, this.pipeline);
  }

  timeZoneURL() {
    return new XsTimeZoneURL(this._analytics, this.pipeline);
  }

  map(
    container: string | HTMLElement,
    options: atlas.ServiceOptions &
      atlas.StyleOptions &
      atlas.UserInteractionOptions &
      (atlas.CameraOptions | atlas.CameraBoundsOptions)
  ) {
    const map = new XsMap(container, options);
    map.events.add('ready', () => {
      this._analytics.updateEventCount(Enums.AnalyticsEvent.MAP_READY);
    });
    return map;
  }
}

class XsTimeZoneURL extends atlasService.TimezoneURL {
  constructor(private analytics: AnalyticsService, pipeline, mapsUrl?: string) {
    super(pipeline, mapsUrl);
  }
  getTimezoneById(
    aborter: Aborter,
    id: string,
    options?: atlasService.GetTimezoneByIdOptions
  ): Promise<atlasService.GetTimezoneByIdResponse> {
    this.analytics.updateEventCount(Enums.AnalyticsEvent.TIME_ZONE_INFO);
    return super.getTimezoneById(aborter, id, options);
  }
}

class XsRouteURL extends atlasService.RouteURL {
  constructor(private analytics: AnalyticsService, pipeline: Pipeline, mapsUrl?: string) {
    super(pipeline, mapsUrl);
  }

  calculateRouteDirections(
    aborter: Aborter,
    coordinates: GeoJSON.Position[],
    options?: CalculateRouteDirectionsOptions
  ): Promise<atlasService.CalculateRouteDirectionsResponse> {
    this.analytics.updateEventCount(Enums.AnalyticsEvent.CALCULATE_ROUTE_DIRECTION);
    return super.calculateRouteDirections(aborter, coordinates, options);
  }
}

class XsSearchURL extends atlasService.SearchURL {
  constructor(private analytics: AnalyticsService, pipeline: Pipeline, mapsUrl?: string) {
    super(pipeline, mapsUrl);
  }

  searchAddressReverse(
    aborter: Aborter,
    position: GeoJSON.Position,
    options?: SearchAddressReverseOptions
  ): Promise<SearchAddressReverseResponse> {
    this.analytics.updateEventCount(Enums.AnalyticsEvent.SEARCH_ADDRESS_REVERSE);
    return super.searchAddressReverse(aborter, position, options);
  }

  searchFuzzy(aborter: Aborter, query: string | GeoJSON.Position, options?: SearchFuzzyOptions) {
    this.analytics.updateEventCount(Enums.AnalyticsEvent.SEARCH_FUZZY);
    return super.searchFuzzy(aborter, query, options);
  }
}

class XsMap extends atlas.Map {
  constructor(
    container: string | HTMLElement,
    options: atlas.ServiceOptions &
      atlas.StyleOptions &
      atlas.UserInteractionOptions &
      (atlas.CameraOptions | atlas.CameraBoundsOptions)
  ) {
    super(container, options);
  }

  setCamera(
    options?: (atlas.CameraOptions | (atlas.CameraBoundsOptions & { pitch?: number; bearing?: number })) &
      atlas.AnimationOptions
  ): void {
    if (options) {
      options.maxPitch = Number.MIN_VALUE;
    }
    super.setCamera(options);
  }
}
